import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { logger } from "@/lib/logger";
import { getSupabase } from "../utils";
import type {
  CommunicationsGoalConfig,
  CommunicationsGoalAutomation,
  SaveMyCommunicationsGoalInput,
} from "./entity-automation-types";
import { AUTOMATION_KINDS, COMMUNICATIONS_GOALS_ENTITY_TYPE } from "./entity-automation-types";
import { resolveLinkedUserIds } from "@/lib/communications-goals/evaluator";

function parseCommunicationsGoalConfig(value: unknown): CommunicationsGoalConfig | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  const cfg = value as Partial<CommunicationsGoalConfig>;
  const targets = cfg.weeklyTargets;
  const notifications = cfg.notifications;
  if (
    !targets ||
    typeof targets.emails !== "number" ||
    typeof targets.calls !== "number" ||
    typeof targets.meetings !== "number" ||
    !notifications ||
    typeof notifications.postDaily !== "boolean" ||
    typeof notifications.postToSalesChannel !== "boolean"
  ) {
    return null;
  }
  // Merge the canonical array with any legacy single-account link into one
  // normalized `linkedUserIds` array so the UI only ever deals with the array.
  const linkedUserIds = resolveLinkedUserIds(cfg);
  return {
    weeklyTargets: {
      emails: targets.emails,
      calls: targets.calls,
      meetings: targets.meetings,
      ...(typeof targets.texts === "number" ? { texts: targets.texts } : {}),
    },
    notifications: {
      postDaily: notifications.postDaily,
      postToSalesChannel: notifications.postToSalesChannel,
    },
    ...(linkedUserIds.length > 0 ? { linkedUserIds } : {}),
  };
}

export function useMyCommunicationsGoalAutomationConfig() {
  return useQuery({
    queryKey: ["communications-goal-automation", "me"],
    queryFn: async (): Promise<CommunicationsGoalAutomation | null> => {
      const supabase = getSupabase();
      const {
        data: { user },
        error: authError,
      } = await supabase.auth.getUser();
      if (authError || !user?.id) {
        throw new Error("User not authenticated");
      }

      const { data, error } = await supabase
        .from("entity_automation_config")
        .select("*")
        .eq("automation_kind", AUTOMATION_KINDS.communicationsGoal)
        .eq("created_by", user.id)
        .order("updated_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) {
        throw error;
      }
      if (!data) {
        return null;
      }

      const config = parseCommunicationsGoalConfig(data.goal_config);
      if (!config) {
        return null;
      }

      return {
        id: data.id,
        enabled: Boolean(data.enabled),
        config,
        createdBy: data.created_by ?? null,
        updatedAt: data.updated_at,
      };
    },
  });
}

export function useSaveMyCommunicationsGoalAutomationConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: SaveMyCommunicationsGoalInput) => {
      const supabase = getSupabase();
      const {
        data: { user },
        error: authError,
      } = await supabase.auth.getUser();
      if (authError || !user?.id) {
        throw new Error("User not authenticated");
      }

      const goalConfig: CommunicationsGoalConfig = {
        weeklyTargets: {
          emails: Math.max(0, input.weeklyTargets.emails),
          calls: Math.max(0, input.weeklyTargets.calls),
          meetings: Math.max(0, input.weeklyTargets.meetings),
          ...(typeof input.weeklyTargets.texts === "number"
            ? { texts: Math.max(0, input.weeklyTargets.texts) }
            : {}),
        },
        notifications: {
          postDaily: input.postDaily,
          postToSalesChannel: input.postToSalesChannel,
        },
        // Canonical multi-account list; deduped + falsy-filtered. Legacy
        // single `linkedUserId` is never written by new saves.
        linkedUserIds: Array.isArray(input.linkedUserIds)
          ? [...new Set(input.linkedUserIds.filter((id) => typeof id === "string" && id.length > 0))]
          : [],
      };

      const payload = {
        entity_type: COMMUNICATIONS_GOALS_ENTITY_TYPE,
        entity_id: user.id,
        automation_kind: AUTOMATION_KINDS.communicationsGoal,
        enabled: input.enabled,
        frequency: "daily" as const,
        day_of_week: 1,
        run_on_business_days_only: false,
        selected_days_of_week: [],
        use_title_template: true,
        task_title_template: "Weekly communications accountability for {entity_name}",
        task_title_fixed: null,
        task_description_template: null,
        assigned_user_ids: [],
        tags: [],
        priority: "medium" as const,
        goal_config: goalConfig,
      };

      const { data: existing } = await supabase
        .from("entity_automation_config")
        .select("id")
        .eq("entity_type", COMMUNICATIONS_GOALS_ENTITY_TYPE)
        .eq("entity_id", user.id)
        .eq("automation_kind", AUTOMATION_KINDS.communicationsGoal)
        .maybeSingle();

      if (existing?.id) {
        const { data, error } = await supabase
          .from("entity_automation_config")
          .update(payload as any)
          .eq("id", existing.id)
          .select("*")
          .single();
        if (error) {
          throw error;
        }
        return data;
      }

      const { data, error } = await supabase
        .from("entity_automation_config")
        .insert({
          ...payload,
          created_by: user.id,
        } as any)
        .select("*")
        .single();
      if (error) {
        throw error;
      }
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["communications-goal-automation", "me"] });
      void queryClient.invalidateQueries({ queryKey: ["entity-automation-configs"] });
      toast.success("Weekly communications goal saved");
    },
    onError: (error) => {
      logger.error("Failed to save communications goal automation config", error);
      toast.error("Failed to save weekly communications goal");
    },
  });
}
